import axios from 'axios';
import { API_CONFIG, buildExternalAiUrl } from './apiConfig';
import { getExternalAiAuthorizationHeaders } from './externalAiAuthHeaders';

/**
 * Mint a client-side correlation id for one upload attempt. Sent as the
 * `session_id` body field on both the real upload and the capture POST so the
 * backend can join the persisted original artifact to the matching
 * `upload_failed` analytics event and surface it as detail.uploadedFileUrl.
 *
 * Per-attempt (not per-session) so a fresh id is used on each retry.
 *
 * @returns {string}
 */
export const generateUploadCorrelationId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through to the timestamp-based id */
  }
  return `mf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

/**
 * Upload floor plan image to external AI service
 * @param {File} imageFile - The image file to upload
 * @returns {Promise} - Response with job ID or immediate result
 */
export const uploadFloorPlan = async (imageFile, options = {}) => {
  try {
    const formData = new FormData();
    formData.append('image', imageFile);
    const listingRef = options?.customer_ref || options?.customerRef || options?.listingId;
    if (listingRef && String(listingRef).trim()) {
      const ref = String(listingRef).trim();
      formData.append('customer_ref', ref);
      formData.append('listingId', ref);
      formData.append('listing_id', ref);
    }
    // Correlation id for the capture-first join. Additive: when absent the
    // request is byte-identical to before. Carried on the real upload so a
    // server-stage failure's captured artifact joins via the same key.
    const sessionId = options?.sessionId || options?.session_id;
    if (sessionId && String(sessionId).trim()) {
      formData.append('session_id', String(sessionId).trim());
    }
    // Stable per-browser guest id (mf_gsid). Persisted server-side on the
    // created layout so a guest who closes the page before processing finishes
    // can still have the upload attributed/claimed to them at a later sign-in.
    // Additive: when absent the request is byte-identical to before.
    const guestSessionId = options?.guestSessionId || options?.guest_session_id;
    if (guestSessionId && String(guestSessionId).trim()) {
      formData.append('guest_session_id', String(guestSessionId).trim());
    }

    // Build headers - only include API credentials if they are defined
    const headers = {
      'Content-Type': 'multipart/form-data',
      ...getExternalAiAuthorizationHeaders(),
    };
    
    // Add API key headers only if credentials are configured
    if (API_CONFIG.API_KEY && API_CONFIG.API_SECRET) {
      headers['X-API-Key'] = API_CONFIG.API_KEY;
      headers['X-API-Secret'] = API_CONFIG.API_SECRET;
    }

    const response = await axios.post(
      buildExternalAiUrl(API_CONFIG.ENDPOINTS.UPLOAD),
      formData,
      {
        headers,
        timeout: 120000, 
      }
    );

    return response.data;
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
};

/**
 * Check job status
 * @param {string} jobId - Job ID to check
 * @returns {Promise} - Job status response
 */
export const checkJobStatus = async (jobId) => {
  try {
    // Build headers - only include API credentials if they are defined
    const headers = {
      ...getExternalAiAuthorizationHeaders(),
    };
    
    if (API_CONFIG.API_KEY && API_CONFIG.API_SECRET) {
      headers['X-API-Key'] = API_CONFIG.API_KEY;
      headers['X-API-Secret'] = API_CONFIG.API_SECRET;
    }

    const response = await axios.get(
      buildExternalAiUrl(`${API_CONFIG.ENDPOINTS.STATUS}/${jobId}`),
      {
        headers,
        timeout: 10000, // 10 seconds
      }
    );

    return response.data;
  } catch (error) {
    console.error('Status check error:', error);
    throw error;
  }
};

/**
 * Capture-first: send the raw bytes of a CLIENT-REJECTED upload to the
 * dedicated capture endpoint so the backend persists the original artifact
 * keyed by `session_id`. That endpoint stores bytes only — no AI processing —
 * and accepts any MIME up to its cap. The backend joins the stored artifact to
 * the upload_failed analytics event (detail.sessionId -> detail.uploadedFileUrl).
 *
 * Fire-and-forget by contract — callers MUST swallow rejections. A capture
 * failure (cross-origin partner embed, oversize, abort, network blip) must
 * never surface to the uploader or change the validation message they saw.
 *
 * The correlation id rides in the `session_id` body field (not a custom header)
 * so a cross-origin embed preflight isn't blocked by an allowedHeaders list.
 *
 * @param {File} imageFile
 * @param {{
 *   sessionId: string,
 *   customerRef?: string|null,
 *   errorType?: string|null,
 *   errorMessage?: string|null,
 *   signal?: AbortSignal,
 * }} options
 * @returns {Promise} capture response, e.g. { stored, uploadAttemptId, sessionId }
 */
export const captureUploadArtifact = async (imageFile, options = {}) => {
  const sessionId = options?.sessionId || options?.session_id;
  if (!imageFile || !sessionId || !String(sessionId).trim()) {
    throw new Error('captureUploadArtifact requires a file and a sessionId');
  }

  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('session_id', String(sessionId).trim());
  const listingRef = options?.customerRef || options?.customer_ref || options?.listingId;
  if (listingRef && String(listingRef).trim()) {
    const ref = String(listingRef).trim();
    formData.append('customer_ref', ref);
    formData.append('listingId', ref);
    formData.append('listing_id', ref);
  }
  if (options?.errorType) formData.append('error_type', String(options.errorType));
  if (options?.errorMessage) formData.append('error_message', String(options.errorMessage));

  const headers = {
    'Content-Type': 'multipart/form-data',
    ...getExternalAiAuthorizationHeaders(),
  };
  if (API_CONFIG.API_KEY && API_CONFIG.API_SECRET) {
    headers['X-API-Key'] = API_CONFIG.API_KEY;
    headers['X-API-Secret'] = API_CONFIG.API_SECRET;
  }

  const response = await axios.post(
    buildExternalAiUrl(API_CONFIG.ENDPOINTS.CAPTURE),
    formData,
    {
      headers,
      timeout: 120000,
      signal: options?.signal,
    }
  );

  return response.data;
};

// Keep backward compatibility
export const processFloorPlanWithExternalAI = uploadFloorPlan;

const externalAiService = {
  uploadFloorPlan,
  captureUploadArtifact,
  generateUploadCorrelationId,
  checkJobStatus,
  processFloorPlanWithExternalAI,
};
export default externalAiService;
